// TODO: описать типы

import { format, subDays, subHours } from 'date-fns';
import { DATE_FORMATS } from 'Utils/formats';
import { UTCtoTimeZone } from 'Utils/UTCConverter';
import { ChartWidgetData, CommonChartInfo, PartWidgetData } from './types';

export const msInHour = 1000 * 60 * 60;
export const msIn10Minutes = 1000 * 60 * 10;
export const EMPTY_ARRAY = [];

export const CHART_TYPES = {
  LINE: 'line',
  BAR: 'bar',
};

const calcModuleParams = {
  calc: true,
  includeOffGridData: true,
  round: true,
};

const initialDateWidget = {
  startDate: format(subHours(new Date(), 12), DATE_FORMATS.api),
  endDate: format(new Date(), DATE_FORMATS.api),
};

const initialDateChart = {
  startDate: format(subDays(new Date(), 7), DATE_FORMATS.api),
  endDate: format(new Date(), DATE_FORMATS.api),
};

export const chartWidgetInfo = (zone: string): CommonChartInfo => ({
  startDate: UTCtoTimeZone(initialDateChart.startDate, zone, DATE_FORMATS.api),
  endDate: UTCtoTimeZone(initialDateChart.endDate, zone, DATE_FORMATS.api),
  ...calcModuleParams,
});

export const commonWidgetInfo = (zone: string) => ({
  startDate: UTCtoTimeZone(initialDateWidget.startDate, zone, DATE_FORMATS.api),
  endDate: UTCtoTimeZone(initialDateWidget.endDate, zone, DATE_FORMATS.api),
  ...calcModuleParams,
});

export function sourceIdSetter(sourceId: number) {
  return (data: PartWidgetData) => ({
    ...data,
    sourceId,
  });
}

export function sourceIdSetterForDataInfo(sourceId: number) {
  return (dataset: ChartWidgetData) => ({
    ...dataset,
    dataInfo: {
      ...dataset.dataInfo,
      sourceId,
    },
  });
}

export function getQuery(options: any) {
  const { commonDataInfo, datasetsInfo } = options;

  const { startDate, endDate, requestSeriesItems, ...rest } = commonDataInfo;

  return {
    ...rest,
    doMeasureBreak: false,
    requestSeriesItems: (datasetsInfo ? datasetsInfo.map((datasetInfo: any) => datasetInfo.dataInfo) : requestSeriesItems)
      .map((item: any) => ({
        startDate,
        endDate,
        ...item,
      })),
  };
}

export function changeScaleDisplayByDatasetsProperties(scales: any, datasetsProperties: any) {
  const newScales = { ...scales };
  const keys = Object.keys(newScales).filter((key) => key.startsWith('y'));

  keys.forEach((key) => {
    const scale = newScales[key];
    const isScaleRequired = datasetsProperties.some((datasetProperties: any) => {
      const isScaleEqual = datasetProperties.dataset.yAxisID === key;
      return isScaleEqual && !datasetProperties.isHidden;
    });

    newScales[key] = {
      ...scale,
      display: isScaleRequired,
    };
  });

  return newScales;
}

export function extractMeteocodesIds(datasetsInfo: ChartWidgetData[]) {
  return datasetsInfo.map((dataset) => {
    const { dataInfo } = dataset;
    return dataInfo.meteoCode;
  });
}

export function getChartData(
  datasetsInfo: ChartWidgetData[],
  data: any,
  meteocodes: any[],
  firstYKey: any,
  convertData: any,
) {
  const isReady = datasetsInfo.length === data.length && datasetsInfo.length === meteocodes.length;
  return {
    datasets: !isReady ? [] : datasetsInfo.map((dataset, i) => {
      const { response } = data[i];
      let dsData = response.map((r: { t: string, v: string }) => ({ timeUTC: r.t, value: r.v })) ?? [];
      const dsMeteocode = meteocodes[i];
      const unitInfo = getUnitInfo(dsMeteocode);
      let yScaleId = `y-${unitInfo.key}`;
      const label = dataset.style.forceLabel || dsMeteocode.code.description;

      if (firstYKey && yScaleId === firstYKey) {
        yScaleId = 'y';
      }

      if (dataset.style.stepped && dataset.style.fixStep) {
        dsData = fixSteppedData(dataset, dsData);
      }

      if (convertData) {
        dsData = dsData.map(convertData);
      }

      return {
        ...dataset.style,
        label,
        data: dsData,
        yAxisID: yScaleId,
        tooltip: {
          callbacks: {
            label: createTooltipLabelCallback(unitInfo.unit),
          },
        },
      };
    }),
  };
}

export function getChartScales(datasetsInfo: ChartWidgetData[], meteocodes: any[]) {
  if (datasetsInfo.length !== meteocodes.length) {
    return undefined;
  }

  const chartjsScales: {
    [key: string]: any
  } = {};

  datasetsInfo.forEach((_, index) => {
    const dsMeteocode = meteocodes[index];
    const unitInfo = getUnitInfo(dsMeteocode);
    const scaleId = `y-${unitInfo.key}`;
    const isPositionLeft = index % 2 === 0;

    if (!Object.keys(chartjsScales).includes(scaleId)) {
      chartjsScales[scaleId] = transformToChartjsYScale(unitInfo.unit, unitInfo.name, isPositionLeft);
    }
  });

  if (Object.keys(chartjsScales).length === 0) {
    return undefined;
  }

  const keys = Object.keys(chartjsScales);
  let firstYKey = null;

  if (!keys.find((key) => key === 'y')) {
    firstYKey = keys.find((key) => key.startsWith('y-'));
    if (firstYKey) {
      chartjsScales.y = chartjsScales[firstYKey];
      delete chartjsScales.y.grid;
      delete chartjsScales[firstYKey];
    }
  }

  chartjsScales.x = {};

  return { chartjsScales, firstYKey };
}

export function getTimeUtcDataConverterByZone(zone: string) {
  if (!zone) {
    return null;
  }
  const key = 'timeUTC';

  return (data: { timeUTC: string, value: string }) => {
    if (!data) {
      return data;
    }

    const value = data[key];
    let newValue = value;

    if (newValue) {
      newValue = UTCtoTimeZone(value, zone, DATE_FORMATS.api);
    }

    return {
      ...data,
      [key]: newValue,
    };
  };
}

export function getUnitInfo(meteocode: any) {
  const { unit } = meteocode.code;
  return {
    key: unit.id,
    unit: unit.unit,
    name: unit.unitClass,
  };
}

export function fixSteppedData(datasetInfo: ChartWidgetData, data: any[]) {
  const fixedData = [...data];

  let fn = null;
  let index = null;

  if (datasetInfo.style.stepped === 'before') {
    fn = fixedData.push;
    index = fixedData.length - 1;
  } else if (datasetInfo.style.stepped === 'after') {
    fn = fixedData.unshift;
    index = 0;
  }

  if (!fn) {
    return fixedData;
  }

  let value;

  if (datasetInfo.style.fixStep && index) {
    value = datasetInfo.style.fixStep(fixedData[index]);
  }

  if (value !== undefined && value !== null) {
    fn.call(fixedData, value);
  }

  return fixedData;
}

export function createTooltipLabelCallback(unit: string) {
  return (context: any) => {
    let label = context.dataset.label || '';

    if (label) {
      label += ': ';
    }
    if (context.formattedValue !== null && context.formattedValue !== undefined) {
      label += `${context.formattedValue} ${unit}`;
    }
    return label;
  };
}

export function transformToChartjsYScale(unit: string, name: string, isPositionLeft = true) {
  return {
    type: 'linear',
    display: true,
    position: isPositionLeft ? 'left' : 'right',
    ticks: {
      callback(value: number | string) {
        return value + unit;
      },
    },
    grid: {
      borderWidth: 0.5,
      borderDash: [5, 10],
    },
    title: {
      display: true,
      text: name,
    },
  };
}
